import {
  memo,
  ReactElement,
  RefObject,
  SyntheticEvent,
  useId,
  useMemo
} from 'react';
import { useSelector } from 'react-redux';
import { Popover } from 'antd';
import cc from 'classcat';
import { convert } from 'utils/betslip/odd-formats-helper';
import { EIconSize } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { fireEvent } from 'utils/generic/event-bus';
import { isMobile } from 'utils/is-mobile';
import { calculateBoostedOdds } from 'utils/sportsbook/calculate-boosted-odds';
import { getIndicatorPosition } from 'utils/sportsbook/get-inidcator-position';
import { Event, Game, Market } from 'interfaces/sportsbook-data-levels';
import { ConditionalWrapper } from 'components/ConditionalWrapper';
import { GlobalIcon } from 'components/GlobalIcon';
import { ERecaptchaEventBusActionTypes } from 'components/Recaptcha/types';
import { ConfirmationPopover } from 'components/Shared/ConfirmationPopover/ConfirmationPopover';
import { OddIndicator } from 'newcomponents/Shared/sportsbook/OddIndicator';
import { useHandleOddSelect } from 'hooks/betslip/useHandleOddSelect';
import { useOddChange } from 'hooks/useOddChange';
import { usePrevious } from 'hooks/usePrevious';
import { useEventbus } from 'newhooks/generic/useEventbus';
import { useOddButtonPosition } from 'newhooks/sportsbook/useOddButtonPosition';
import { EBoostType } from 'store/reducers/sport-data';
import { getRecaptchaEnabled } from 'store/selectors/app-data';
import {
  getEventIds,
  getOddFormat,
  getQuickBetEnabled,
  getShouldShowQuickBetInfoPopover
} from 'store/selectors/betslip';
import { getBoostedOdds } from 'store/selectors/sport-data';
import './index.less';

type TProps = {
  game: Game;
  event: Event;
  market: Market;
  sportId: number;
  isLive: boolean;
  competitionName: string;
  sportAlias?: string;
  isUsa?: boolean;
  dataTestId?: string;
};

export const Odd = memo(
  ({
    game,
    event,
    market,
    sportId,
    sportAlias,
    competitionName,
    isLive,
    isUsa,
    dataTestId
  }: TProps): ReactElement => {
    const uniqueId = useId();

    const isQuickBetEnabled = useSelector(getQuickBetEnabled);
    const shouldShowQuickBetInfoPopover = useSelector(
      getShouldShowQuickBetInfoPopover
    );

    const oddFormat = useSelector(getOddFormat);
    const boostedOdds = useSelector(getBoostedOdds);
    const { handleOddSelect } = useHandleOddSelect(sportId);
    const selectedEventIds = useSelector(getEventIds);
    const recaptchaEnabled = useSelector(getRecaptchaEnabled);
    const prevPrice = usePrevious(event.price);

    const itemSelected = useMemo(
      () => selectedEventIds.includes(Number(event.id)),
      [selectedEventIds, event.id]
    );

    const addToBetSlip = () => {
      handleOddSelect(
        undefined,
        game,
        event,
        competitionName,
        [market],
        sportAlias || '',
        isUsa ?? false
      );
    };

    const { oddChangeHandler, popover, hidePopover } = useOddChange(
      () => {},
      addToBetSlip,
      selectedEventIds.map(id => `${id}`)
    );

    const { buttonRef, getButtonPosition } = useOddButtonPosition();
    const hasBoostedOdd =
      game.id &&
      boostedOdds?.[game.id]?.[event.id] &&
      (boostedOdds?.[game.id]?.[event.id].type === EBoostType.ALL ||
        boostedOdds?.[game.id]?.[event.id].type ===
          (isLive ? EBoostType.LIVE : EBoostType.PREMATCH));

    const indicatorValue = useMemo(() => {
      return getIndicatorPosition(prevPrice, event.price);
    }, [event.price, prevPrice]);

    const onChangeHandlerRecaptchaWrapper = (e: SyntheticEvent) => {
      e.stopPropagation();

      if (event.price) {
        if (
          isQuickBetEnabled &&
          !shouldShowQuickBetInfoPopover &&
          recaptchaEnabled
        ) {
          fireEvent({
            key: ERecaptchaEventBusActionTypes.Validate,
            payload: uniqueId
          });

          return;
        }

        oddChangeHandler(`${event.id}`, event.price);
      }
    };

    useEventbus(
      {
        [ERecaptchaEventBusActionTypes.Verified]: ({
          payload
        }: {
          payload: string;
        }) => {
          if (payload === uniqueId) {
            oddChangeHandler(`${event.id}`, event.price);
          }
        }
      },
      [oddChangeHandler]
    );

    return (
      <ConditionalWrapper
        condition={!popover?.validateRecaptcha}
        wrapper={children => (
          <Popover
            content={<ConfirmationPopover {...(popover || {})} />}
            placement={getButtonPosition()}
            trigger="click"
            visible={Boolean(popover?.id && `${popover?.id}` === `${event.id}`)}
            onVisibleChange={(visible: boolean) => !visible && hidePopover()}
            overlayClassName={cc([
              'quick-bet-confirmation-popover',
              {
                'quick-bet-confirmation-popover-right':
                  isMobile() && getButtonPosition() === 'topRight',
                'quick-bet-confirmation-popover-hide':
                  popover?.validateRecaptcha
              }
            ])}
            destroyTooltipOnHide
          >
            {children}
          </Popover>
        )}
      >
        <div
          ref={buttonRef as RefObject<HTMLDivElement>}
          className={cc([
            'x-odd__wrapper',
            `x-odd__wrapper--${indicatorValue}`,
            {
              'x-odd__wrapper--checked': itemSelected,
              'x-odd__wrapper--withBase': !!event.base,
              'x-odd__wrapper--mobile': isMobile()
            }
          ])}
          onClick={onChangeHandlerRecaptchaWrapper}
          {...(dataTestId && { 'data-testId': dataTestId })}
        >
          {event.base && (
            <div className="x-odd__base">
              {(['Over', 'Under'].includes(event.type) ||
                ['Over', 'Under'].includes(event.type_1)) &&
                `${event.type?.[0] || event.type_1?.[0]}/`}
              {event.base}
            </div>
          )}
          <div
            className={cc([
              'x-odd__price',
              {
                'x-odd__price--withBase': !!event.base
              }
            ])}
          >
            {event.price ? (
              game.is_blocked ? (
                <GlobalIcon
                  lib="generic"
                  name="lock"
                  theme="default"
                  size={EIconSize._11}
                  className="isBlocked-odd"
                />
              ) : (
                <div>
                  {hasBoostedOdd ? (
                    <div
                      className={cc([
                        'boostedOdd boostedOdd-USA',
                        { 'boostedOdd-USA--mobile': isMobile() }
                      ])}
                    >
                      <GlobalIcon
                        lib="generic"
                        name="boostedOdds"
                        theme="colored"
                        size={EIconSize._14}
                        skeleton={{ shape: 'circle' }}
                        className={cc([
                          'boostedOdd-USA--icon',
                          { 'boostedOdd-USA--icon--mobile': isMobile() }
                        ])}
                      />
                      <div
                        className={cc([
                          'boostedOdd--header boostedOdd-USA--header',
                          { 'boostedOdd-USA--header--mobile': isMobile() }
                        ])}
                      >
                        {convert(event.price, oddFormat)}
                        {+SpringConfigs.PREVIOUS_BOOSTED_ODDS ? (
                          <span
                            className={cc([
                              'boostedOdd__calculation boostedOdd-USA__calculation',
                              {
                                'boostedOdd-USA__calculation--mobile':
                                  isMobile()
                              }
                            ])}
                          >
                            {convert(
                              calculateBoostedOdds(event.price),
                              oddFormat
                            )}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    convert(event.price, oddFormat)
                  )}
                </div>
              )
            ) : (
              '-'
            )}
            {!!prevPrice && (
              <OddIndicator
                indicatorValue={indicatorValue}
                withBase
                isUsaLayout
                key={indicatorValue}
              />
            )}
          </div>
        </div>
      </ConditionalWrapper>
    );
  }
);
